import {Link} from 'react-router-dom';
import {ProfileResponse} from '../../store/backendApi';
import {Trip} from '../../schema';
import {fullImage, imgUrl} from '../../utils/images';
import config from '../../config';

interface TripHeaderProps {
  trip: Trip;
  ownerProfile?: ProfileResponse;
}

/**
 * Presentational component for the trip hero header section.
 * Displays header image, title, description, and author profile link.
 */
export const TripHeader = ({ trip, ownerProfile }: TripHeaderProps) => {
  return (
    <div className="relative h-[65vh] w-full overflow-hidden">
      <img
        src={
          trip.headerFileName
            ? imgUrl(trip.id, fullImage(trip.headerFileName))
            : '/default-trip-header.jpg'
        }
        alt="Trip header"
        loading="eager"
        // @ts-ignore
        fetchpriority="high"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: 'center 30%' }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-neutral-darkest to-transparent" />

      <div className="absolute bottom-0 left-0 w-full px-4 md:px-8 pb-12 pt-24 max-w-7xl mx-auto">
        <div>
          <h1
            className="text-4xl md:text-6xl lg:text-7xl text-on-dark tracking-tight mb-6 leading-tight"
            style={{
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            }}
          >
            {trip.title}
          </h1>

          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-8">
            {/* Author */}
            <Link
              to={`/profile/${trip.ownerId}`}
              className="flex items-center gap-3 group transition-transform hover:scale-105 origin-left"
            >
              <div className="relative">
                <img
                  src={
                    `${config.media.cdnUrl}/users/${trip.ownerId}` +
                    `-thumbnail.jpg?v=${Date.now()}`
                  }
                  className="w-14 h-14 rounded-full border-2 border-subtle object-cover shadow-md group-hover:border-surface"
                  alt="Author"
                  loading="eager"
                  onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
                />
              </div>
              <div>
                <span className="block text-xs text-on-brand tracking-wider ">
                  {ownerProfile?.username ?? ''}
                </span>
                <span className="text-on-dark ">View Profile</span>
              </div>
            </Link>

            {/* Description */}
            {trip.description && (
              <div className="text-lg md:text-xl text-on-brand font-light leading-relaxed max-w-2xl border-l-2 border-subtle pl-4 md:pl-6">
                {trip.description}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
